import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, Send, Loader2, ShieldCheck, Lock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { useAuth } from "../../contexts/AuthContext";
import { getBaseline, approveBaseline, publishBaseline, getSegments } from "../../lib/api";
import { useToast } from "../../contexts/ToastContext";
import { formatDate, formatBytes } from "../../lib/utils";

const STATE_VARIANT = { DRAFT: "muted", APPROVED: "info", PUBLISHED: "success", SUPERSEDED: "secondary" };

const STATE_FLOW = [
  { state: "DRAFT", label: "Draft", desc: "Uploaded — awaiting approval" },
  { state: "APPROVED", label: "Approved", desc: "Admin approved — ready to publish" },
  { state: "PUBLISHED", label: "Published", desc: "Live — fleet can download" },
];

// Stepper caption colour: the current step is highlighted, completed steps are
// darker than the ones still ahead.
function stepLabelClass(active, done) {
  if (active) return "text-blue-600";
  if (done) return "text-slate-600";
  return "text-slate-400";
}

export default function BaselineDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canAccess } = useAuth();
  const { toast } = useToast();

  const [baseline, setBaseline] = useState(null);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    Promise.all([getBaseline(id), getSegments()]).then(([b, s]) => {
      setBaseline(b);
      setSegments(s);
      setLoading(false);
    });
  }, [id]);

  const handleApprove = async () => {
    setApproving(true);
    try {
      await approveBaseline(
        id,
        "I confirm that I have reviewed this baseline and authorise it for publication to the Tetra fleet."
      );
      setBaseline((prev) => ({ ...prev, state: "APPROVED" }));
      toast({ title: "Baseline approved", description: `v${baseline.version} is now ready to publish.`, variant: "success" });
    } catch {
      toast({ title: "Approval failed", variant: "destructive" });
    } finally {
      setApproving(false);
    }
  };

  const handlePublish = async () => {
    if (!selectedSegment) return;
    setPublishing(true);
    try {
      await publishBaseline(id, selectedSegment);
      const seg = segments.find((s) => s.id === selectedSegment);
      setBaseline((prev) => ({
        ...prev,
        state: "PUBLISHED",
        targetSegment: selectedSegment,
        targetSegmentName: seg?.name,
        publishedAt: new Date().toISOString(),
      }));
      setPublishDialogOpen(false);
      toast({ title: "Baseline published", description: `v${baseline.version} is published and the other operators have been notified. No device receives it until a rollout is targeted.`, variant: "success" });
    } catch {
      toast({ title: "Publish failed", variant: "destructive" });
    } finally {
      setPublishing(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Loading…</div>;
  if (!baseline) return <div className="p-8 text-center text-slate-400">Baseline not found.</div>;

  const currentStepIdx = STATE_FLOW.findIndex((s) => s.state === baseline.state);

  return (
    <div className="max-w-3xl space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/firmware")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold">Baseline v{baseline.version}</h2>
          <Badge variant={STATE_VARIANT[baseline.state]}>{baseline.state}</Badge>
          {baseline.mandatory && <Badge variant="info">Mandatory</Badge>}
        </div>
      </div>

      {/* State progress bar */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex items-center gap-0">
            {STATE_FLOW.map((step, i) => {
              const done = i <= currentStepIdx;
              const active = i === currentStepIdx;
              return (
                <React.Fragment key={step.state}>
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 ${
                      done ? "bg-blue-600 border-blue-600 text-white" : "border-slate-200 text-slate-400 bg-white"
                    }`}>
                      {done ? <CheckCircle className="h-4 w-4" /> : i + 1}
                    </div>
                    <p className={`text-xs mt-1 font-medium ${stepLabelClass(active, done)}`}>
                      {step.label}
                    </p>
                    <p className="text-[11px] text-slate-400 text-center max-w-[90px]">{step.desc}</p>
                  </div>
                  {i < STATE_FLOW.length - 1 && (
                    <div className={`flex-1 h-0.5 mb-6 mx-1 ${i < currentStepIdx ? "bg-blue-600" : "bg-slate-200"}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Metadata */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">File Details</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row label="Filename" value={<code className="text-xs bg-slate-50 px-1.5 py-0.5 rounded">{baseline.filename}</code>} />
            <Row label="Size" value={formatBytes(baseline.size)} />
            <Row label="SHA-256" value={
              <code className="text-[11px] bg-slate-50 px-1.5 py-0.5 rounded break-all">{baseline.sha256}</code>
            } />
            <div className="flex items-center gap-1.5 text-emerald-700 text-xs bg-emerald-50 rounded px-2 py-1.5">
              <ShieldCheck className="h-3.5 w-3.5" />
              <span>Asymmetric KMS signature verified at upload</span>
            </div>
            <div className="flex items-center gap-1.5 text-blue-700 text-xs bg-blue-50 rounded px-2 py-1.5">
              <Lock className="h-3.5 w-3.5" />
              <span>Stored SSE-KMS · S3 Object versioning enabled</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Lifecycle</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row label="Uploaded by" value={baseline.uploadedBy} />
            <Row label="Uploaded at" value={formatDate(baseline.uploadedAt)} />
            <Row label="Approved by" value={baseline.approvedBy || "—"} />
            <Row label="Approved at" value={formatDate(baseline.approvedAt)} />
            <Row label="Published at" value={formatDate(baseline.publishedAt)} />
            <Row label="Target segment" value={baseline.targetSegmentName || "—"} />
          </CardContent>
        </Card>
      </div>

      {/* Release notes */}
      <Card>
        <CardHeader><CardTitle className="text-base">Release Notes</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{baseline.releaseNotes}</p>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-3 flex-wrap">
        {canAccess("FleetAdmin") && baseline.state === "DRAFT" && (
          <Button onClick={handleApprove} disabled={approving}>
            {approving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
            Approve Baseline
          </Button>
        )}
        {canAccess("ReleaseManager", "FleetAdmin") && baseline.state === "APPROVED" && (
          <Button onClick={() => setPublishDialogOpen(true)}>
            <Send className="mr-2 h-4 w-4" /> Publish to Segment
          </Button>
        )}
        {baseline.state === "PUBLISHED" && (
          <Alert variant="success" className="flex-1">
            <AlertDescription>
              This baseline is <strong>PUBLISHED</strong> and the other operators have been notified.
              No device receives it until a rollout is targeted for its country.
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Publish dialog */}
      <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish v{baseline.version}</DialogTitle>
            <DialogDescription>
              Select the fleet segment to target. The rollout tier chosen at publish caps how many devices may be targeted:
              Canary 5% → 25% → 100%.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Target Segment</Label>
            <Select onValueChange={setSelectedSegment}>
              <SelectTrigger>
                <SelectValue placeholder="Choose segment…" />
              </SelectTrigger>
              <SelectContent>
                {segments.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name} ({s.deviceCount.toLocaleString()} devices)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedSegment && (
              <Alert variant="info">
                <AlertDescription className="text-xs">
                  Canary 5% → Extended 25% → Full Fleet 100%. Widening requires the current tier to have reported clean.
                  Rollback rate &gt; 5% will auto-pause the rollout.
                </AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishDialogOpen(false)}>Cancel</Button>
            <Button onClick={handlePublish} disabled={!selectedSegment || publishing}>
              {publishing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="text-slate-900 text-right">{value}</span>
    </div>
  );
}

function Label({ children }) {
  return <p className="text-sm font-medium text-slate-700">{children}</p>;
}
